import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import AuthContext from "./auth-context";
import { API_NEW_URL } from "../constants/constants";
import storage from "../hooks/storage";
import {
  DOMAIN_CONFIG_CHANGED_EVENT,
  DOMAIN_CONFIG_STORAGE_KEY,
} from "../utils/domainConfig";

// Tenant-level entitlement for the Cobrowse Add-On. The Zendesk app setting
// `enable_cobrowse` (see MainTabs) is the admin's local toggle; this flag is
// the Blitzz-side license gate, controlled from the Super Admin Portal. Both
// must be true for Cobrowse to actually work.
//
// Fetching strategy: fetch once whenever the auth token changes. That covers
// initial login (loginHandler sets token), app reload with a still-valid
// stored token (auth-context seeds token from storage), and refresh-token
// success (refresh calls loginHandler → setToken). We do NOT refetch on
// Cobrowse tab click — the value changes rarely and the paid-feature gate
// should not add latency to every tab interaction.
//
// Fail-closed: on network/error we treat the tenant as NOT entitled. UI-config
// flakes are fine to fail-open, but licensing gates should never grant a paid
// feature to an uncertain tenant.
export const TenantConfigContext = React.createContext({
  isLoading: true,
  hasError: false,
  hasCobrowseAddOn: false,
  configurations: null,
  refresh: () => {},
});

const deriveCobrowseAddOn = (configurations) =>
  !!configurations &&
  configurations.cobrowse === "1" &&
  configurations.cobrowseintegrations === "1";

const TenantConfigContextProvider = (props) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx.token;

  // Seed from localStorage so reloads render with the last-known config
  // instantly. Agent portal does the same via `storage.get(DOMAIN_CONFIGURATION)`
  // + `setHeadersFlag` (Blitzz_Agent/src/apis/api.js:610-650).
  const cached = storage.get(DOMAIN_CONFIG_STORAGE_KEY);
  const cachedConfigurations = cached?.configurations || null;

  const [state, setState] = useState({
    isLoading: !cachedConfigurations,
    hasError: false,
    hasCobrowseAddOn: deriveCobrowseAddOn(cachedConfigurations),
    configurations: cachedConfigurations,
  });

  // In-flight guard so overlapping API responses that all signal an SA change
  // only trigger a single refetch (agent portal uses `domainConfigurationApiObject`
  // for the same purpose, api.js:563-587).
  const inFlightRef = useRef(false);

  const fetchConfigurations = useCallback((activeToken) => {
    if (!activeToken) {
      setState({
        isLoading: false,
        hasError: false,
        hasCobrowseAddOn: false,
        configurations: null,
      });
      return Promise.resolve();
    }

    if (inFlightRef.current) return Promise.resolve();
    inFlightRef.current = true;
    setState((prev) => ({ ...prev, isLoading: true }));

    return fetch(API_NEW_URL + "domains/configurations", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + activeToken,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          setState({
            isLoading: false,
            hasError: true,
            hasCobrowseAddOn: false,
            configurations: null,
          });
          return;
        }
        const body = await res.json();
        const configurations =
          (body && body.data && body.data.configurations) ||
          (body && body.configurations) ||
          null;
        const hasCobrowseAddOn = deriveCobrowseAddOn(configurations);

        // Persist the same shape agent portal caches: full data body with
        // `configurationmodifiedat` merged from response headers. Subsequent
        // API responses compare their header against this cached timestamp.
        const modifiedAt = res.headers.get("configurationmodifiedat");
        const payload = (body && body.data) || body || {};
        storage.set(DOMAIN_CONFIG_STORAGE_KEY, {
          ...payload,
          configurationmodifiedat: modifiedAt,
        });

        setState({
          isLoading: false,
          hasError: false,
          hasCobrowseAddOn,
          configurations,
        });
      })
      .catch(() => {
        setState({
          isLoading: false,
          hasError: true,
          hasCobrowseAddOn: false,
          configurations: null,
        });
      })
      .finally(() => {
        inFlightRef.current = false;
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchConfigurations(token).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [token, fetchConfigurations]);

  // SA-change hook: any Blitzz API response whose `configurationmodifiedat`
  // header is newer than our cached one dispatches this event
  // (see utils/domainConfig.js `maybeRefreshDomainConfig`). We refetch here
  // rather than in the helper so React state stays the single source of truth.
  useEffect(() => {
    const onChange = () => fetchConfigurations(token);
    window.addEventListener(DOMAIN_CONFIG_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(DOMAIN_CONFIG_CHANGED_EVENT, onChange);
  }, [token, fetchConfigurations]);

  const contextValue = {
    ...state,
    refresh: () => fetchConfigurations(token),
  };

  return (
    <TenantConfigContext.Provider value={contextValue}>
      {props.children}
    </TenantConfigContext.Provider>
  );
};

export default TenantConfigContextProvider;
